import { Fragment, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@voll-intelligence/ui-kit/ui/tooltip';
import {
  AirplaneLandingIcon,
  AirplaneTakeoffIcon,
  type Icon as PhosphorIcon,
} from '@phosphor-icons/react';
import { AirlineLogo } from '../../../shared/airline_logo/airline_logo.js';
import { arrivalDayOffset, fmtTime, fmtWeekdayDayMonth } from '../flight_format.js';
import type { CityOfAirport } from '../filter_logic.js';
import { carrierNameOf, type FlightSegmentSource } from '../flight_offer_source.js';
import './flight_offers.css';

/** Sentido do trecho na lista de ofertas. `leg` (multi-trecho) leva SEMPRE o
 *  glifo de decolagem: num roteiro de N trechos nenhum deles é "a volta". */
export type FlightLegKind = 'outbound' | 'return' | 'leg';

/** Os textos da perna, por prop (#4997). O genui-kit monta em
 *  `flightLegCopy(locale)`. */
export type FlightLegCopy = {
  /** A condição de um trecho sem escala ("Direto"). */
  readonly direct: string;
  /** A condição com escalas ("1 parada", "2 paradas"). */
  readonly stops: (count: number) => string;
  /** O link da perna para o detalhe do voo. */
  readonly viewDetail: string;
  /** O texto do "+N" de chegada em dia seguinte, para tooltip e leitor de tela. */
  readonly arrivesNextDay: (days: number) => string;
  /** A palavra do sentido no head da perna densa ("Ida", "Volta"). */
  readonly kind: Readonly<Record<FlightLegKind, string>>;
};

/** "+N" de chegada em dia seguinte, para tooltip e rótulo acessível; vazio no
 *  mesmo dia. Sem o texto o glifo é lido como "mais um". */
function nextDayLabel(dayOffset: number, copy: FlightLegCopy): string {
  return dayOffset > 0 ? copy.arrivesNextDay(dayOffset) : '';
}

/** Frase de condição do trecho (Direto / N paradas) — MESMA regra nas TRÊS
 *  gramáticas (`FlightLegTrip` empilhada, `FlightLegDense` no head, e a linha do
 *  voo escolhido na tela de tarifas): singular só quando `stops === 1`. Uma
 *  função só para as três não divergirem sobre o mesmo fato. */
export function stopCondition(
  stops: number,
  copy: Pick<FlightLegCopy, 'direct' | 'stops'>,
): string {
  return stops <= 0 ? copy.direct : copy.stops(stops);
}

/**
 * **A perna de voo — uma gramática para o recibo, e uma para a lista.**
 *
 * Esta forma nasceu privada dentro da linha de ofertas (`FlightOfferRow`) e o
 * resumo da reserva desenhava a MESMA informação com outra gramática
 * (`.fqc-seg`: condição em cima, duração embaixo, sem nós de escala). Duas
 * gramáticas para o mesmo fato é o que fazia o card de checkout não parecer com
 * o card de oferta — por isso este componente virou a fonte única para
 * recibo/checkout/bilhete/detalhe de voo (ver usos abaixo).
 *
 * **A lista de ofertas tem a SUA própria, `FlightLegDense`** (mais abaixo neste
 * arquivo): a reforma de densidade subiu paradas e duração para o head da
 * perna e reduziu o trecho a uma linha — mudança de ÁRVORE, não de estilo, que
 * não cabe como prop nesta gramática (ver o porquê no docblock dela). O split
 * é escopado DE PROPÓSITO à lista: as outras superfícies (recibo, checkout,
 * bilhete, drawer de detalhe) continuam na gramática empilhada abaixo, que não
 * muda com esta reforma.
 *
 * Nesta gramática (`FlightLeg`), o que varia entre as superfícies são dois
 * opt-ins, e nada mais:
 *
 * - `showCarrier` — o nome da companhia ao lado do nº do voo. A LINHA DE OFERTAS
 *   o dispensa de propósito (logo + prefixo do número já dizem a cia duas vezes,
 *   e a linha compete por largura com o trilho de preço); o resumo da reserva e o
 *   bilhete o mantêm, porque ali a pessoa confere o que comprou, não escolhe.
 * - `onDetail` — o "Detalhes" DENTRO da perna. Na oferta o link mora no rodapé
 *   da linha (`.fq-why`), compartilhado com a nota do agente; nos cards de
 *   recibo cada perna abre o seu, então ele desce para o rodapé da própria perna.
 *
 * O CONTÊINER não mora aqui: a oferta empilha as pernas em duas colunas
 * (`.fq-legs`, ida | volta lado a lado com o trilho de preço à direita), os cards
 * de recibo empilham na vertical (`.fq-legs--stack`, N trechos). Quem sabe a
 * forma da viagem é o host.
 *
 * Display-only — projeção do trecho, sem estado de domínio.
 */
export function FlightLeg({
  seg,
  stops,
  kindLabel,
  flightNumber,
  showCarrier = false,
  onDetail,
  copy,
  locale,
}: {
  readonly seg: FlightSegmentSource;
  /** Paradas do trecho. O chamador decide a fonte: a oferta tem `offer.stops`
   *  (autoritativo do produtor); os cards de recibo só têm o segmento e contam
   *  `seg.connections`. */
  readonly stops: number;
  /** Rótulo do sentido já traduzido ("Ida"/"Volta"/"Trecho 2"). Ausente = perna
   *  única, e aí o rótulo seria a terceira repetição do que o bloco já diz. */
  readonly kindLabel?: string;
  /** Nº do voo pronto. Ausente → derivado do segmento (`flightNumberLabel`). */
  readonly flightNumber?: string;
  /** Escreve o nome da companhia antes do nº do voo. */
  readonly showCarrier?: boolean;
  /** Abre o drawer "Detalhe do voo" DESTA perna; ausente = sem link. */
  readonly onDetail?: () => void;
  readonly copy: FlightLegCopy;
  /** O idioma das datas. */
  readonly locale: string;
}) {
  const carrier = showCarrier ? carrierNameOf(seg) : '';
  return (
    <div className="fq-leg">
      <div className="fq-leg-head">
        {/* Data primeiro e em ink: é o dado ÚNICO da linha. `Ida`/`Volta` é
            dito três vezes no card (posição do bloco, par de aeroportos,
            rótulo) — rótulo triplamente redundante não pode pesar mais. */}
        <span className="fq-leg-date">{fmtWeekdayDayMonth(seg.departAt, locale)}</span>
        {kindLabel && <span className="fq-leg-kind">{kindLabel}</span>}
        <span className="fq-logo">
          {/* 24 e não a AIRLINE_MARK_SIZE (22): o desenho calibra o logo do card de
            ofertas no dobro do texto do header (2 × 12). As outras superfícies de
            voo seguem na constante. */}
          <AirlineLogo
            {...(seg.carrier ? { iata: seg.carrier } : {})}
            {...(seg.carrierName ? { name: seg.carrierName } : {})}
            size={24}
          />
        </span>
        {carrier && <span className="fq-name">{carrier}</span>}
        {/* **Nº do voo, e não o nome da companhia.** A logo mais o prefixo do
            número já identificam a cia duas vezes, então escrever o nome é a
            terceira — e o número é o que a pessoa usa pra conferir o voo no
            Google Flights antes de aceitar o preço. Nos cards de recibo
            (`showCarrier`) o nome volta: ali não se escolhe, se confere. */}
        <span className="fq-no">
          {flightNumber ?? flightNumberLabel(seg.carrier, seg.flightNumber)}
        </span>
      </div>
      <FlightLegTrip seg={seg} stops={stops} copy={copy} />
      {onDetail && (
        <div className="fq-leg-foot">
          <button type="button" className="fq-detail" onClick={onDetail}>
            {copy.viewDetail}
          </button>
        </div>
      )}
    </div>
  );
}

/** Trecho central da linha: horário/aeroporto de partida → conexão → chegada.
 *  Os dois ramos têm a MESMA gramática — duração acima, linha, condição abaixo
 *  (Direto / N paradas) — e diferem só em a linha carregar nós: com escalas
 *  reais (`seg.connections`, buscadas eager só nas ofertas curadas) desenha um
 *  nó por parada (avião no círculo + IATA + layover) sobre ela. Os horários das
 *  duas pernas alinham pela altura compartilhada da célula do grid (ver
 *  `.fq-leg`/`.fq-leg .fq-trip` no CSS), não por tirar os nós do fluxo. */
function FlightLegTrip({
  seg,
  stops,
  copy,
}: {
  readonly seg: FlightSegmentSource;
  readonly stops: number;
  readonly copy: FlightLegCopy;
}) {
  const conns = seg.connections ?? [];
  const dayOffset = arrivalDayOffset(seg.departAt, seg.arriveAt);
  const arrivesLabel = nextDayLabel(dayOffset, copy);
  // A condição do voo é a MESMA frase nos dois ramos — o que muda é só se a
  // linha carrega nós. Com nós ela diz apenas a contagem: os IATAs e os
  // layovers já estão escritos nos próprios nós, e repeti-los aqui embaixo
  // seria dizer a mesma coisa duas vezes dentro de 200px.
  const condition = stopCondition(stops, copy);
  return (
    <div className="fq-trip">
      <div className="fq-pt fq-pt-dep">
        {/* Horário e IATA no MESMO peso e na MESMA linha: é o par que faz a
            pessoa conferir o aeroporto junto com o horário, e é ele que carrega
            a guarda contra embarcar no lugar errado. Não há sub-rótulo de
            cidade: resolver IATA→cidade custaria o mapa-múndi gerado
            (~7k entradas) no bundle da WebView por um rótulo decorativo. */}
        <span className="fq-when">
          <span className="fq-time">{fmtTime(seg.departAt)}</span>
          <span className="fq-code">{seg.from}</span>
        </span>
      </div>
      {conns.length > 0 ? (
        <div className="fq-conn has-nodes">
          {seg.durationLabel && <span className="fq-conn-dur">{seg.durationLabel}</span>}
          <span className="fq-conn-track">
            {/* As mesmas bolinhas do ramo direto: a gramática da linha é uma só,
                o que muda é ela carregar nós. Aqui saem do fluxo (CSS) pra não
                disputar o `space-around` que distribui as escalas. */}
            <span className="fq-conn-cap" />
            {conns.map((c, i) => (
              <span className="fq-stop" key={`${c.iata}-${String(i)}`}>
                <span className="fq-stop-dot">
                  <AirplaneTakeoffIcon size={11} aria-hidden />
                </span>
                <span className="fq-stop-meta">
                  <span className="fq-stop-code">{c.iata}</span>
                  {c.layoverLabel && <span className="fq-stop-lay">{c.layoverLabel}</span>}
                </span>
              </span>
            ))}
            <span className="fq-conn-cap" />
          </span>
          <span className="fq-stops">{condition}</span>
        </div>
      ) : (
        <div className="fq-conn">
          {seg.durationLabel && <span className="fq-conn-dur">{seg.durationLabel}</span>}
          {/* `●────────●` — as bolinhas marcam os extremos do trecho como num
              mapa de transporte, e é o que fecha a leitura da linha: o traço
              sozinho liga dois pontos sem dizer onde começa e onde termina. */}
          <span className="fq-conn-rail">
            <span className="fq-conn-cap" />
            <span className="fq-conn-line" />
            <span className="fq-conn-cap" />
          </span>
          {/* `direto` em ink, e não em muted: em corporativo é o atributo que
              fecha a decisão depois do horário. Os dois estados vêm em ink; o
              que muda é a palavra. */}
          <span className="fq-stops">{condition}</span>
        </div>
      )}
      <div className="fq-pt">
        <span className="fq-when">
          <span className="fq-time">{fmtTime(seg.arriveAt)}</span>
          {/* `+1` quando a chegada cai no dia seguinte. Sem ele um voo noturno
              (22:10 → 06:05) parece chegar na mesma madrugada, o que é um erro
              de FATO, não de estilo: muda a diária de hotel e a reunião que a
              pessoa marcou. O glifo carrega o texto traduzido (`title` e rótulo
              acessível) porque "mais um" sozinho não diz nada num leitor. */}
          {dayOffset > 0 && (
            <span className="fq-plus1" role="img" title={arrivesLabel} aria-label={arrivesLabel}>
              +{dayOffset}
            </span>
          )}
          <span className="fq-code">{seg.to}</span>
        </span>
      </div>
    </div>
  );
}

/**
 * **Uma escala do trilho denso — pílula + tooltip do conteúdo completo.**
 *
 * A pílula elipsa o texto quando a linha aperta (muitas escalas ou layover
 * longo) e o `.fui-card` corta em `overflow: hidden`; o tooltip do kit (popup
 * por portal) devolve o conteúdo completo — aeroporto · layover — sem ser
 * clipado. O mouse revela no hover; o teclado, no foco.
 *
 * **`tabIndex` só entra QUANDO a pílula de fato trunca (medido).** Sem corte o
 * texto já está todo à vista, e um tab stop por escala — nenhuma delas acionável
 * — poluiria a navegação por teclado da lista. Medir `scrollWidth > clientWidth`
 * (e remedir no reflow via `ResizeObserver`: card estreita no mobile / ida e
 * volta, ou o logo carrega) mantém o foco casado com o corte real. O texto segue
 * inteiro no DOM da pílula (a elipse é só visual), então leitor de tela já o
 * anuncia com ou sem foco — o foco é como quem enxerga e navega por teclado
 * alcança o que a elipse escondeu. `TooltipTrigger render` faz a própria pílula
 * ser o gatilho (nada de wrapper novo no flex do trilho).
 */
/** A casca da pílula do card do celular (#3874), em utilitárias do DS. */
const CARD_PILL =
  'relative min-w-0 truncate rounded-full border border-border bg-card px-2 py-1 text-detail font-medium text-foreground';

export function StopPill({
  label,
  variant = 'rail',
  children,
  focusable = false,
}: {
  /** O conteúdo COMPLETO da escala — é o tooltip, e o texto da pílula quando
   *  não há `children`. */
  readonly label: string;
  /** Conteúdo visível com marcação própria (o card do celular pesa o IATA e
   *  não o tempo). Ausente ⇒ a pílula mostra o `label`. */
  readonly children?: ReactNode;
  /** A casca da pílula: o trilho denso (`.fq-stop-pill` do sheet) ou o card
   *  do celular (#3874, utilitárias do DS). */
  readonly variant?: 'rail' | 'card';
  /** O tooltip diz algo que o texto visível NÃO diz mesmo sem truncar (o nome do
   *  aeroporto da escala, #4157): a pílula vira alvo de foco para o teclado. */
  readonly focusable?: boolean;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const [clipped, setClipped] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    // A new label is new text to measure (the box may not change with it).
    const measure = () => {
      setClipped(label !== '' && el.scrollWidth > el.clientWidth);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => {
      ro.disconnect();
    };
  }, [label]);
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <span
            ref={ref}
            className={variant === 'card' ? CARD_PILL : 'fq-stop-pill'}
            {...(clipped || focusable ? { tabIndex: 0 } : {})}
          />
        }
      >
        {children ?? label}
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

/**
 * A cidade sob o IATA, na lista de ofertas (#2363). Nula fora do cadastro do
 * domain — e aí o nó fica VAZIO em vez de sumir: repetir o código no lugar da
 * cidade seria dizer duas vezes a mesma coisa, inventar um nome seria pior, e
 * remover o nó desalinharia os horários das duas pontas (a `.fq-pt` é coluna, e
 * uma ponta com duas linhas ao lado de outra com uma faz o horário escorregar).
 * Reservar a linha é o que mantém `19:45 GIG` e `20:35 GRU` na mesma altura
 * quando só um dos lados resolve.
 *
 * A guarda contra embarcar no aeroporto errado continua sendo o IATA no peso do
 * horário; a cidade é o desempate quando a praça tem mais de um aeroporto
 * (GRU × CGH, GIG × SDU), que é onde o erro de fato acontece.
 */
function CityUnder({ iata, cityOf }: { readonly iata: string; readonly cityOf: CityOfAirport }) {
  return <span className="fq-city">{cityOf(iata) ?? ''}</span>;
}

export const KIND_GLYPH: Record<FlightLegKind, PhosphorIcon> = {
  outbound: AirplaneTakeoffIcon,
  return: AirplaneLandingIcon,
  leg: AirplaneTakeoffIcon,
};

/**
 * **A perna da LISTA DE OFERTAS.** Mesma informação que a `FlightLeg`, outra
 * distribuição: a condição do voo (direto / N paradas) e a duração total sobem
 * para o head, ao lado da data, e o trecho fica com UMA linha — as pontas e o
 * trilho, nada empilhado. É o que tira 28px da linha.
 *
 * Irmã e não uma prop `dense` da `FlightLeg`: o head e o trecho trocam de
 * ÁRVORE, não de estilo. Um booleano que substitui metade do DOM são dois
 * componentes escondidos atrás de um parâmetro, e o arquivo fica ilegível para
 * os dois casos.
 *
 * Não tem `onDetail`: na oferta o "Detalhes" mora no rodapé da LINHA
 * (`.fq-why`), compartilhado com a nota do agente.
 *
 * **`kind` VOLTOU a ser visível (#2363), e a reversão é deliberada.** A reforma
 * de densidade tinha largado `Ida`/`Volta` da tela — o rótulo era dito três
 * vezes (posição da coluna, par de aeroportos, rótulo) e largá-lo era o que
 * fazia o head caber numa coluna de ida e volta —, deixando só um `ariaLabel`
 * invisível para leitor de tela. O frame 445-32443 os quer de volta com glifo, e
 * a medição a 270px no Storybook é o que autoriza: o head é `nowrap` +
 * `overflow: hidden`, então o aperto não estoura a caixa, ele ELIPSA a
 * `.fq-leg-sum` ("Direto · 50min"). Se a medição mostrar a condição cortada, o
 * caminho é largar a palavra e ficar com o glifo — ele já diz o sentido.
 *
 * Com a palavra em cena o `aria-label` saiu: o grupo se nomeia pelo próprio
 * texto (`Ida · Qua, 24 jun`), que diz mais do que o rótulo dizia. Perna única
 * não passa `kind` e não vira grupo — não há sentido a anunciar.
 */
export function FlightLegDense({
  seg,
  stops,
  flightNumber,
  kind,
  copy,
  locale,
  cityOf,
}: {
  readonly seg: FlightSegmentSource;
  /** Paradas do trecho — `offer.stops` é autoritativo do produtor. */
  readonly stops: number;
  /** Nº do voo pronto. Ausente → derivado do segmento. */
  readonly flightNumber?: string;
  /** Sentido do trecho: escolhe o glifo e a palavra. Ausente = perna única
   *  (one-way), que não tem sentido a distinguir — sem glifo, sem palavra e sem
   *  grupo (não há nada a anunciar). Substituiu o `ariaLabel`, que existia só
   *  porque a palavra era invisível; com ela em cena o grupo se nomeia pelo
   *  próprio texto. */
  readonly kind?: FlightLegKind;
  readonly copy: FlightLegCopy;
  /** O idioma das datas. */
  readonly locale: string;
  /** A cidade sob o IATA; o host passa o cadastro dele. */
  readonly cityOf: CityOfAirport;
}) {
  const dayOffset = arrivalDayOffset(seg.departAt, seg.arriveAt);
  const arrivesLabel = nextDayLabel(dayOffset, copy);
  const condition = stopCondition(stops, copy);
  // O separador é do PAR, não do fragmento: sem duração ele viraria um `·`
  // órfão pendurado depois da condição.
  const summary = seg.durationLabel ? `${condition} · ${seg.durationLabel}` : condition;
  const Glyph = kind ? KIND_GLYPH[kind] : null;
  const kindWord = kind ? copy.kind[kind] : null;
  // `aria-labelledby` e não `aria-label`: `role="group"` NÃO deriva nome do
  // conteúdo (não está na lista de roles "name from content" da ARIA), então sem
  // isto o grupo ficaria anônimo. Apontar para o nó VISÍVEL faz o nome ser
  // exatamente o que se lê na tela — `aria-label` seria uma segunda cópia do
  // mesmo texto, e cópias divergem.
  const headId = useId();
  return (
    <div
      className="fq-leg fq-leg--dense"
      {...(kind ? { role: 'group' as const, 'aria-labelledby': headId } : {})}
    >
      <div className="fq-leg-head">
        {/* Glifo, palavra e data num nó só (`.fq-leg-date`): são um bloco de
            leitura, e um irmão a mais neste head — que é `nowrap` +
            `overflow: hidden` — roubaria largura da `.fq-leg-sum`, a única com
            `text-overflow: ellipsis`. O custo cairia em "Direto · 50min". */}
        <span className="fq-leg-date" {...(kind ? { id: headId } : {})}>
          {Glyph && (
            <span className="fq-leg-glyph" aria-hidden="true">
              <Glyph size={13} />
            </span>
          )}
          {kindWord && <span className="fq-leg-way">{kindWord} · </span>}
          {fmtWeekdayDayMonth(seg.departAt, locale)}
        </span>
        <span className="fq-leg-sum">{summary}</span>
        <span className="fq-logo">
          {/* 20 e não os 24 da perna legada: o head denso tem 16px de linha, e o
              logo calibra abaixo dela em vez do dobro do texto. */}
          <AirlineLogo
            {...(seg.carrier ? { iata: seg.carrier } : {})}
            {...(seg.carrierName ? { name: seg.carrierName } : {})}
            size={20}
          />
        </span>
        <span className="fq-no">
          {flightNumber ?? flightNumberLabel(seg.carrier, seg.flightNumber)}
        </span>
      </div>
      <div className="fq-trip">
        <div className="fq-pt fq-pt-dep">
          <span className="fq-when">
            <span className="fq-time">{fmtTime(seg.departAt)}</span>
            <span className="fq-code">{seg.from}</span>
          </span>
          {/* IRMÃ da `.fq-when`, nunca filha: a `.fq-when` é
              `align-items: baseline` e um filho a mais cairia NA linha do
              horário. */}
          <CityUnder iata={seg.from} cityOf={cityOf} />
        </div>
        {/* `TooltipProvider` não emite DOM (é só contexto do kit), então a
            `.fq-conn` continua filha direta da `.fq-trip` e o flex do trilho não
            muda. `delay` compartilhado: passado o primeiro hover, pular de uma
            escala pra outra revela na hora. */}
        <TooltipProvider delay={120}>
          <div className="fq-conn">
            {/* `● ──[GRU · 1h15]──[CNF · 45min]── ●` — as bolinhas marcam os
                extremos do trecho e cada escala pousa SOBRE a linha, não abaixo
                dela. Sem escalas o laço não roda e sobra `●────●`: a mesma
                gramática, sem nada a dizer no meio. A contagem já está no head,
                então o trilho liso não perde informação — antes ela custava uma
                terceira linha empilhada. */}
            <span className="fq-conn-cap" />
            {(seg.connections ?? []).map((c, i) => (
              <Fragment key={`${c.iata}-${String(i)}`}>
                <span className="fq-conn-line" />
                {/* Conteúdo COMPLETO da escala (aeroporto · layover) — o mesmo
                    texto da pílula e do tooltip. */}
                <StopPill label={c.layoverLabel ? `${c.iata} · ${c.layoverLabel}` : c.iata} />
              </Fragment>
            ))}
            <span className="fq-conn-line" />
            <span className="fq-conn-cap" />
          </div>
        </TooltipProvider>
        <div className="fq-pt">
          <span className="fq-when">
            <span className="fq-time">{fmtTime(seg.arriveAt)}</span>
            {dayOffset > 0 && (
              <span className="fq-plus1" role="img" title={arrivesLabel} aria-label={arrivesLabel}>
                +{dayOffset}
              </span>
            )}
            <span className="fq-code">{seg.to}</span>
          </span>
          <CityUnder iata={seg.to} cityOf={cityOf} />
        </div>
      </div>
    </div>
  );
}

/** Paradas de um trecho quando a única fonte é o próprio segmento (cards de
 *  recibo): a contagem de escalas, 0 = direto. A lista de ofertas NÃO usa isto —
 *  lá `offer.stops` é autoritativo do produtor. */
export function segmentStops(seg: Pick<FlightSegmentSource, 'connections'>): number {
  return seg.connections?.length ?? 0;
}

/** Nº do voo pra exibição. O provider às vezes já entrega o número prefixado com
 *  o código da cia ("LA 3900"), outras só o número ("3900"). Reprefixar cegamente
 *  geraria "LA LA 3900" — então só prefixa quando o número ainda não começa com o
 *  código da cia (case-insensitive). */
export function flightNumberLabel(carrier?: string, flightNumber?: string): string {
  if (!flightNumber) return carrier ?? '';
  if (!carrier) return flightNumber;
  return flightNumber.toLowerCase().startsWith(carrier.toLowerCase())
    ? flightNumber
    : `${carrier} ${flightNumber}`;
}
